import { useState, useEffect, createContext, useContext } from 'react';

// 主题上下文
const ThemeContext = createContext();

// 主题配置
const themes = {
  light: {
    name: 'light',
    colors: {
      background: '#F7F8F6',
      foreground: '#151515',
      primary: '#E9EBDF',
      secondary: '#CBCCC4',
      muted: '#8B867F',
      border: '#433E38',
      accent: '#0E352C',
    }
  },
  dark: {
    name: 'dark',
    colors: {
      background: '#151515',
      foreground: '#E9EBDF',
      primary: '#E9EBDF',
      secondary: '#CBCCC4',
      muted: '#8B867F',
      border: '#433E38',
      accent: '#0E352C',
    }
  }
};

/**
 * 主题 Provider 组件
 */
export function ThemeProvider({ children, defaultTheme = 'dark', storageKey = 'ui-theme' }) {
  const [theme, setTheme] = useState(defaultTheme);

  useEffect(() => {
    // 从 localStorage 读取保存的主题
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(storageKey);
      if (stored && themes[stored]) {
        setTheme(stored);
      } else {
        // 如果没有保存的主题，检查系统偏好
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        setTheme(prefersDark ? 'dark' : 'light');
      }
    }
  }, [storageKey]);

  useEffect(() => {
    // 保存主题到 localStorage
    if (typeof window !== 'undefined') {
      localStorage.setItem(storageKey, theme);
    }

    // 更新 HTML 根元素的主题属性
    if (typeof document !== 'undefined') {
      const root = document.documentElement;
      root.classList.remove('light', 'dark');
      root.classList.add(theme);
      
      // 设置 CSS 自定义属性
      const themeConfig = themes[theme];
      if (themeConfig) {
        Object.entries(themeConfig.colors).forEach(([key, value]) => {
          root.style.setProperty(`--color-${key}`, value);
        });
      }
    }
  }, [theme, storageKey]);

  const value = {
    theme,
    themes,
    setTheme,
    currentTheme: themes[theme],
    toggleTheme: () => {
      setTheme(prev => prev === 'light' ? 'dark' : 'light');
    },
    isLight: theme === 'light',
    isDark: theme === 'dark',
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

/**
 * 使用主题的 Hook
 */
export function useTheme() {
  const context = useContext(ThemeContext);
  
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  
  return context;
}

/**
 * 主题切换 Hook
 */
export function useThemeToggle() {
  const { theme, setTheme, toggleTheme } = useTheme();
  
  return {
    theme,
    setTheme,
    toggleTheme,
    setLight: () => setTheme('light'),
    setDark: () => setTheme('dark'),
  };
}

export default useTheme;